import * as React from 'react';
import {
  BackgroundProvider,
  Grid,
  makeStyles,
  Typography,
  BLUE,
  WHITE,
  Link,
  Container,
  Box,
  icons,
  ExpandMore,
  AccordionLegacy,
  AccordionSummary,
  AccordionDetails,
} from '@applyboard/ui-components';

import {
  FOOTER_TWITTER,
  FOOTER_FACEBOOK,
  FOOTER_YOUTUBE,
  FOOTER_INSTAGRAM,
  FOOTER_LINKEDIN,
} from '../../ab-common/src/util/cdnAssets';

const { ApplyBoardLogoIcon } = icons;

const useStyles = makeStyles({
  gridItem: {
    '@media (min-width: 600px) and (max-width: 959.95px)': {
      paddingRight: '16px',
      paddingLeft: '16px',
    },
  },
  gridItemFirst: {
    '@media (min-width: 600px) and (max-width: 959.95px)': {
      paddingLeft: '24px',
    },
    '& .MuiAccordionSummary-root': {
      '@media (max-width: 599.95px)': {
        '&:hover': {
          cursor: 'default',
        },
        '& .MuiAccordionSummary-content': {
          cursor: 'default',
        },
      },
    },
  },
  gridItemLast: {
    '@media (min-width: 600px) and (max-width: 959.95px)': {
      paddingLeft: '0',
    },
  },
  socialSectionWrapper: {
    '@media (max-width: 599.95px)': {
      marginBottom: '36px',
    },
  },
  sectionTitle: {
    fontFamily: 'Montserrat !important',
    fontWeight: '600 !important',
    fontSize: '20px !important',
  },
  sectionDesktop: {
    '@media (max-width: 599.95px)': {
      display: 'none',
    },
  },
  sectionMobile: {
    display: 'none',
    '@media (max-width: 599.95px)': {
      display: 'block',
    },
    '& .MuiAccordion-root': {
      background: 'none',
      boxShadow: 'none',
      '& .MuiAccordionSummary-root': {
        padding: '0',
        minHeight: '0',
        '& .MuiAccordionSummary-content': {
          flexGrow: '0',
          margin: '0',
        },
        '& .MuiAccordionSummary-expandIcon': {
          color: WHITE,
          flex: '1',
          justifyContent: 'start',
          padding: '0 0 0 5px',
        },
      },
    },
    '& .MuiAccordionDetails-root': {
      padding: '0',
      flexDirection: 'column',
      marginTop: '24px',
    },
    '& .Mui-expanded': {
      transform: 'rotate(0deg)',
      '& .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
    '& .MuiPaper-elevation1': {
      margin: '0',
    },
  },
  box: {
    backgroundColor: BLUE[8],
  },
  container: {
    maxWidth: '1440px',
    paddingTop: '44px',
    paddingBottom: '28px',
    '@media (max-width: 959.95px)': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    '& p': {
      lineHeight: '28px !important',
    },
  },
  socialIcons: {
    display: 'flex',
    gap: '24px',
    '@media (max-width: 959.95px)': {
      gap: '14px',
    },
    '& a': {
      alignItems: 'center',
      display: 'inline-flex',
      flex: '0 0 24px',
      justifyContent: 'center',
      filter: 'brightness(5)',
      '&:hover': {
        filter: 'brightness(2)',
      },
    },
  },
  logo: {
    maxWidth: '200px',
    '@media (max-width: 1279.95px)': {
      minWidth: '150px',
      maxWidth: '170px',
      '& svg': {
        verticalAlign: 'bottom',
      },
    },
    '@media (max-width: 959.95px)': {
      '& svg': {
        paddingRight: '16px',
      },
    },
  },
  location: {
    maxWidth: '135px',
    '@media (max-width: 599.95px)': {
      maxWidth: '188px',
    },
  },
  link: {
    '&:hover': {
      '& p': {
        color: '#DEDAD3',
      },
    },
  },
});

type FooterLink = {
  title: string;
  links: {
    title: string;
    link: string;
  }[];
};

type SocialIcon = {
  id: number;
  label: string;
  link: string;
  icon: string;
};

export function Footer() {
  const classes = useStyles();
  const socialIcons: SocialIcon[] = [
    {
      id: 1,
      label: 'LinkedIn',
      link: 'https://www.linkedin.com/company/applyboard',
      icon: FOOTER_LINKEDIN,
    },
    {
      id: 2,
      label: 'Facebook',
      link: 'https://www.facebook.com/applyboard/',
      icon: FOOTER_FACEBOOK,
    },
    {
      id: 3,
      label: 'Instagram',
      link: 'https://www.instagram.com/applyboard',
      icon: FOOTER_INSTAGRAM,
    },
    {
      id: 4,
      label: 'X (Twitter)',
      link: 'https://twitter.com/ApplyBoard',
      icon: FOOTER_TWITTER,
    },
    {
      id: 5,
      label: 'YouTube',
      link: 'https://www.youtube.com/ApplyBoardChannel',
      icon: FOOTER_YOUTUBE,
    },
  ];

  const productLinks: FooterLink = {
    title: 'Product',
    links: [
      {
        title: 'Students',
        link: 'https://www.applyboard.com/studyabroad',
      },
      {
        title: 'Schools',
        link: 'https://www.applyboard.com/institution',
      },
      {
        title: 'Recruiters',
        link: 'https://www.applyboard.com/recruitment-partners',
      },
    ],
  };

  const discoverLinks: FooterLink = {
    title: 'Discover',
    links: [
      {
        title: 'Discover Programs',
        link: 'https://www.applyboard.com/search',
      },
      {
        title: 'Register',
        link: 'https://www.applyboard.com/register',
      },
    ],
  };

  const aboutLinks: FooterLink = {
    title: 'About',
    links: [
      {
        title: 'Our Story',
        link: 'https://www.applyboard.com/our-story',
      },
      {
        title: 'Careers',
        link: 'https://www.applyboard.com/careers',
      },
      {
        title: 'Blog',
        link: 'https://www.applyboard.com/blog',
      },
      {
        title: 'Press',
        link: 'https://www.applyboard.com/press',
      },
      {
        title: 'Contact',
        link: 'https://www.applyboard.com/contact',
      },
    ],
  };

  const resourcesLinks: FooterLink = {
    title: 'Resources',
    links: [
      {
        title: 'Our Solutions',
        link: 'https://www.applyboard.com/360solutions',
      },
      {
        title: 'UK Resources',
        link: 'https://www.applyboard.com/resources/uk',
      },
      {
        title: 'AU Resources',
        link: 'https://www.applyboard.com/resources/australia',
      },
    ],
  };

  const legalLinks: FooterLink = {
    title: 'Legal',
    links: [
      {
        title: 'Legal',
        link: 'https://www.applyboard.com/legal',
      },
      {
        title: 'Privacy & Cookies Policy',
        link: 'https://www.applyboard.com/privacy-and-cookie-policy',
      },
      {
        title: 'Terms & Conditions',
        link: 'https://www.applyboard.com/terms-and-conditions',
      },
      {
        title: 'Accessibility',
        link: 'https://www.applyboard.com/accessibility',
      },
      {
        title: 'ApplyBoard Fees',
        link: 'https://assist.applyboard.com/hc/en-us/articles/6478276149901-ApplyBoard-Fees',
      },
    ],
  };

  const renderFooterLinkSection = (section: FooterLink, isAnchor = false) => {
    const Component = section.links.map((elem, idx) => (
      <Box mb={isAnchor ? 4 : 1.5} key={idx}>
        <Link href={elem.link} className={classes.link} underline="none">
          <Typography className={isAnchor ? classes.sectionTitle : ''}>{elem.title}</Typography>
        </Link>
      </Box>
    ));
    return Component;
  };

  const createLinkSection = (section: FooterLink) => {
    const Component = (
      <>
        <Box mb={4} className={classes.sectionMobile}>
          <AccordionLegacy>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography className={classes.sectionTitle}>{section.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>{renderFooterLinkSection(section)}</AccordionDetails>
          </AccordionLegacy>
        </Box>
        <Box mb={3.75} className={classes.sectionDesktop}>
          <Box mb={3}>
            <Typography className={classes.sectionTitle}>{section.title}</Typography>
          </Box>
          <Box> {renderFooterLinkSection(section)} </Box>
        </Box>
      </>
    );

    return Component;
  };

  return (
    <Box className={classes.box}>
      <Container maxWidth={false} className={classes.container}>
        <BackgroundProvider color="primary">
          <Grid container direction="row">
            <Grid container item direction="column" xs={12} sm={3}>
              <Grid item>
                <Box className={classes.logo}>
                  <Link href="https://www.applyboard.com/">
                    <ApplyBoardLogoIcon />
                  </Link>
                </Box>
              </Grid>

              <Grid item>
                <Box className={classes.location} mb={3} mt={5.75}>
                  <Link
                    underline="none"
                    href="https://goo.gl/maps/DeLXVUrCTVtXzKM57"
                    target="_blank"
                    rel="noopener"
                    className={classes.link}
                  >
                    <Typography>101 Frederick St, Kitchener, ON N2H 6R3</Typography>
                  </Link>
                </Box>
              </Grid>

              <Grid item className={classes.socialSectionWrapper}>
                <Box mt={1.5}>
                  <Typography className={classes.sectionTitle}>Get Social</Typography>
                  <Box className={classes.socialIcons} mt={3}>
                    {socialIcons.map(item => {
                      return (
                        <Link
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          key={`${item.label}-${item.index}`}
                          title={item.label}
                        >
                          <img alt="" src={item.icon} />
                        </Link>
                      );
                    })}
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Grid
              container
              item
              direction="column"
              xs={12}
              sm={3}
              className={`${classes.gridItem} ${classes.gridItemFirst}`}
            >
              <Grid item> {renderFooterLinkSection(productLinks, true)} </Grid>
              <Grid item> {createLinkSection(discoverLinks)} </Grid>
            </Grid>

            <Grid container item direction="column" xs={12} sm={3} className={classes.gridItem}>
              <Grid item> {createLinkSection(aboutLinks)} </Grid>
            </Grid>

            <Grid
              container
              item
              direction="column"
              xs={12}
              sm={3}
              className={`${classes.gridItem} ${classes.gridItemLast}`}
            >
              <Grid item> {createLinkSection(resourcesLinks)} </Grid>
              <Grid item> {createLinkSection(legalLinks)} </Grid>
            </Grid>
          </Grid>
          <Box textAlign="center" mt={3}>
            <Typography>© 2015-{new Date().getFullYear()} ApplyBoard Inc.</Typography>
          </Box>
        </BackgroundProvider>
      </Container>
    </Box>
  );
}
